/**
 * The aim ghost and cursor reticle (clarity-first graft): with a non-empty
 * queue the floor shows what the cast mode the modifiers imply would do
 * right now, drawn at alpha 0.3 in the dominant colour, so the "same queue,
 * different spell per mode" rule is visible before the cast (pillar 4).
 * The ghost switches the moment Shift is held.
 */

import {
  arcSegments, BARRIER, BEAM, lineSegments, MELEE, MINES, PLAYER_RADIUS, ringSegments, rockFlight, rockHeight, rockLanding, rockMuzzle, rockReach, SHARDS,
  SPRAY, WIND, type Arena, type CastMode, type Element, type Segment, type Vec2,
} from '@shared/index.js';
import type { LocalView, ViewPlayer } from '../state/viewModel.js';
import { elementColor } from '../palette.js';
import { circlePath, TAU, UNIT, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';
import { dominantOf } from './entities.js';

function ellipseRing(ctx: Ctx, px: number, py: number, r: number): void {
  ctx.beginPath();
  ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU);
}

/**
 * Ghost of what `mode` would do right now, drawn on the floor at alpha 0.3
 * in the dominant colour. Changes the moment Shift is held (area) so the
 * "same queue, different spell" rule is visible before the cast.
 */
export function drawAimGhost(ctx: Ctx, cam: Camera, me: LocalView, p: ViewPlayer, mode: CastMode, cursor: Vec2, time: number): void {
  const spec = me.preview[mode];
  if (spec.kind === 'none' || p.act !== 'idle') return;
  const u = UNIT;
  const x = p.rx, y = p.ry;
  const aim = Math.atan2(cursor.y - y, cursor.x - x);
  const dom = dominantOf(me.queue);
  const col = dom ? elementColor(dom) : '#FFFFFF';
  const px = cam.sx(x), py = cam.sy(y);
  ctx.save();
  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = col;
  ctx.fillStyle = col;
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 6]);
  ctx.lineDashOffset = -time * 30;
  const line = (len: number, a: number): void => {
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(cam.sx(x + Math.cos(a) * len), cam.sy(y + Math.sin(a) * len));
    ctx.stroke();
  };
  const cone = (len: number, half: number): void => {
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(cam.sx(x + Math.cos(aim - half) * len), cam.sy(y + Math.sin(aim - half) * len));
    ctx.moveTo(px, py);
    ctx.lineTo(cam.sx(x + Math.cos(aim + half) * len), cam.sy(y + Math.sin(aim + half) * len));
    ctx.stroke();
  };
  // a spray's ghost is its true cone (sprayCone.ts): the sides and the edge where it stops, the falloff band marked
  const sprayGhost = (len: number, half: number): void => {
    cone(len, half);
    ctx.beginPath();
    ctx.arc(px, py, len * u, aim - half, aim + half);
    ctx.stroke();
    ctx.globalAlpha = 0.15;
    ctx.beginPath();
    ctx.arc(px, py, len * SPRAY.falloffFrom * u, aim - half, aim + half);
    ctx.stroke();
  };
  const chords = (segs: Segment[]): void => {
    ctx.beginPath();
    for (const [a, b] of segs) { ctx.moveTo(cam.sx(a.x), cam.sy(a.y)); ctx.lineTo(cam.sx(b.x), cam.sy(b.y)); }
    ctx.stroke();
  };
  const geometry = (geo: 'arc' | 'ring' | 'line', r: number): void => {
    const c: Vec2 = { x, y };
    if (geo === 'arc') chords(arcSegments(c, aim, BARRIER.arcDeg, r, 10));
    else if (geo === 'ring') chords(ringSegments(c, r, 24));
    else chords(lineSegments({ x: x + Math.cos(aim) * 1.5, y: y + Math.sin(aim) * 1.5 }, aim, BARRIER.lineLength, 1));
  };
  switch (spec.kind) {
    case 'beam': line(BEAM.range, aim); break;
    case 'spray': sprayGhost(spec.range, spec.halfAngle); break;
    case 'arc': line(spec.range, aim); ellipseRing(ctx, px, py, spec.range * u); ctx.stroke(); break;
    case 'projectile': {
      if (spec.solid === 'ice') { cone(spec.range, SHARDS.spreadMaxDeg * Math.PI / 360); break; }
      // The throw's lane: a tap lands at the near ring, a full hold at the far one (rockThrow.ts); hold to walk it out.
      const near = rockLanding({ x, y }, aim, PLAYER_RADIUS, spec.range, 0);
      const far = rockLanding({ x, y }, aim, PLAYER_RADIUS, spec.range, 1);
      ctx.beginPath();
      ctx.moveTo(cam.sx(near.x), cam.sy(near.y));
      ctx.lineTo(cam.sx(far.x), cam.sy(far.y));
      ctx.stroke();
      ctx.setLineDash([]);
      circlePath(ctx, cam.sx(near.x), cam.sy(near.y), spec.blastRadius * u);
      ctx.stroke();
      ctx.globalAlpha = 0.15;
      circlePath(ctx, cam.sx(far.x), cam.sy(far.y), spec.blastRadius * u);
      ctx.stroke();
      break;
    }
    case 'nova':
    case 'quake': ellipseRing(ctx, px, py, spec.radius * u); ctx.stroke(); break;
    case 'barrier': geometry(spec.geometry, BARRIER.arcRadius); break;
    case 'wall': geometry(spec.geometry, BARRIER.arcRadius); break;
    case 'storm': geometry(spec.geometry, BARRIER.arcRadius); break;
    case 'mines': {
      const r = MINES.arcRadius;
      for (let i = 0; i < spec.count; i++) {
        let mx: number, my: number;
        if (spec.geometry === 'ring') { const a = (TAU * i) / spec.count; mx = x + Math.cos(a) * r; my = y + Math.sin(a) * r; }
        else if (spec.geometry === 'arc') { const a = aim + (-60 + (120 * i) / Math.max(1, spec.count - 1)) * Math.PI / 180; mx = x + Math.cos(a) * r; my = y + Math.sin(a) * r; }
        else { const d = (i - (spec.count - 1) / 2) * MINES.lineSpacing; mx = x + Math.cos(aim) * 1.5 - Math.sin(aim) * d; my = y + Math.sin(aim) * 1.5 + Math.cos(aim) * d; }
        ellipseRing(ctx, cam.sx(mx), cam.sy(my), 0.3 * u);
        ctx.fill();
      }
      break;
    }
    case 'aura': ellipseRing(ctx, px, py, spec.radius * u); ctx.stroke(); break;
    case 'bubble': ellipseRing(ctx, px, py, 0.8 * u); ctx.stroke(); break;
    case 'armor':
    case 'self': ellipseRing(ctx, px, py, 0.6 * u); ctx.stroke(); break;
    case 'melee': {
      ctx.setLineDash([]);
      if (spec.shape === 'swing') { ctx.beginPath(); ctx.moveTo(px, py); ctx.ellipse(px, py, MELEE.swingRadius * u, MELEE.swingRadius * u * 0.45, 0, aim - MELEE.swingArcDeg * Math.PI / 360, aim + MELEE.swingArcDeg * Math.PI / 360); ctx.closePath(); ctx.fill(); }
      else if (spec.shape === 'slash') { ctx.beginPath(); ctx.moveTo(px, py); ctx.ellipse(px, py, MELEE.slashRadius * u, MELEE.slashRadius * u * 0.45, 0, aim - MELEE.slashArcDeg * Math.PI / 360, aim + MELEE.slashArcDeg * Math.PI / 360); ctx.closePath(); ctx.fill(); }
      else { ctx.lineWidth = 6; line(spec.shape === 'thrust' ? MELEE.thrustLength : MELEE.fissureLength, aim); }
      break;
    }
    case 'wind':
      if (spec.geometry === 'cone') cone(WIND.radius, WIND.coneDeg * Math.PI / 360);
      else { ellipseRing(ctx, px, py, WIND.radius * u); ctx.stroke(); }
      break;
  }
  ctx.restore();
}

/**
 * While Force is held on a rock: where it will come down if let go now (rockThrow.ts), as the dotted arc it will
 * fly from the hand and a ring the size of its blast, walking out as the charge grows; a faint ring at the full
 * throw's spot shows how far it can go, and the ring turns white at a full charge. Shards show their fan narrowing.
 */
export function drawThrowPreview(ctx: Ctx, cam: Camera, p: ViewPlayer, charging: NonNullable<LocalView['charging']>, aim: number, arena: Arena, time: number): void {
  const { spec, charge } = charging;
  const u = UNIT;
  const x = p.rx, y = p.ry;
  const col = spec.elements.length > 0 ? elementColor(spec.elements.find((e) => e !== 'earth') ?? spec.elements[0]!) : '#FFFFFF';
  ctx.save();
  if (spec.solid === 'ice') {
    const half = (SHARDS.spreadMaxDeg + (SHARDS.spreadMinDeg - SHARDS.spreadMaxDeg) * charge) * Math.PI / 360;
    ctx.globalAlpha = 0.45;
    ctx.strokeStyle = elementColor('ice');
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 6]);
    for (const s of [-half, half]) {
      ctx.beginPath();
      ctx.moveTo(cam.sx(x), cam.sy(y));
      ctx.lineTo(cam.sx(x + Math.cos(aim + s) * spec.range), cam.sy(y + Math.sin(aim + s) * spec.range));
      ctx.stroke();
    }
    ctx.restore();
    return;
  }
  const f = rockFlight(spec.range, charge);
  const start = rockMuzzle({ x, y }, aim, PLAYER_RADIUS, f.radius);
  // a pillar or wall in the way stops it there, so that is where the ring goes
  const stop = rockReach(arena, start, aim, f.range, f.radius);
  const land = { x: start.x + Math.cos(aim) * stop, y: start.y + Math.sin(aim) * stop };
  const far = rockLanding({ x, y }, aim, PLAYER_RADIUS, spec.range, 1);
  const full = charge >= 1;
  // the full throw's spot, faint
  ctx.globalAlpha = 0.18;
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([3, 5]);
  circlePath(ctx, cam.sx(far.x), cam.sy(far.y), spec.blastRadius * u);
  ctx.stroke();
  // the arc it will fly, dots marching along it
  ctx.setLineDash([]);
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = full ? '#FFFFFF' : col;
  const steps = 14;
  const march = (time * 2) % 1;
  for (let i = 0; i < steps; i++) {
    const t = (i + march) / steps;
    const d = t * stop;
    const z = rockHeight(d, f.range);
    const sx = cam.sx(start.x + Math.cos(aim) * d), sy = cam.sy(start.y + Math.sin(aim) * d) - z * u;
    circlePath(ctx, sx, sy, 1.5 + 1.5 * t);
    ctx.fill();
  }
  // where it lands (or hits): the blast ring, filled a little, pulsing; white once fully charged
  const lx = cam.sx(land.x), ly = cam.sy(land.y) - rockHeight(stop, f.range) * u;
  const pulse = 1 + 0.06 * Math.sin(time * (full ? 16 : 6));
  ctx.globalAlpha = 0.16;
  circlePath(ctx, lx, ly, spec.blastRadius * u * pulse);
  ctx.fill();
  ctx.globalAlpha = 0.9;
  ctx.strokeStyle = full ? '#FFFFFF' : col;
  ctx.lineWidth = 2.5;
  ctx.stroke();
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(lx - 6, ly); ctx.lineTo(lx + 6, ly);
  ctx.moveTo(lx, ly - 6); ctx.lineTo(lx, ly + 6);
  ctx.stroke();
  ctx.restore();
}

/** Cursor reticle: ring + 4 ticks in the dominant element colour (white when the queue is empty); doubled in area mode. */
export function drawCursor(ctx: Ctx, sx: number, sy: number, dom: Element | null, area: boolean, time: number): void {
  const col = dom ? elementColor(dom) : '#FFFFFF';
  ctx.strokeStyle = col;
  ctx.lineWidth = 1.5;
  const r = 9 + Math.sin(time * 6) * 0.8;
  circlePath(ctx, sx, sy, r);
  ctx.stroke();
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const a = (Math.PI / 2) * i;
    ctx.moveTo(sx + Math.cos(a) * (r + 2), sy + Math.sin(a) * (r + 2));
    ctx.lineTo(sx + Math.cos(a) * (r + 7), sy + Math.sin(a) * (r + 7));
  }
  ctx.stroke();
  if (area) {
    ctx.setLineDash([3, 3]);
    circlePath(ctx, sx, sy, r + 6);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.fillStyle = col;
  circlePath(ctx, sx, sy, 1.5);
  ctx.fill();
}
